class ComponentProductFormPurchase extends Theme.Component {
  constructor() {
    super();

    this.defineElements({
      frequency: ".product-form-purchase-frequency",
      frequencyTrigger: ".product-form-purchase-frequency-trigger",
      frequencyList: ".product-form-purchase-frequency-options",
      frequencyValue: "[data-purchase-frequency-value]",
      subscriptionInput: "[data-purchase-type='subscription']",
      onetimeInput: "[data-purchase-type='onetime']",
      onetimeLabel: "[data-purchase-onetime-label]",
      onetimePrice: "[data-purchase-onetime-price]",
      onetimeCompare: "[data-purchase-onetime-compare]"
    });

    this.defineElementCollections({
      options: ".product-form-purchase",
      packs: ".product-form-purchase-pack",
      frequencyOptions: ".product-form-purchase-frequency-option"
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.addDirectEvent("click", document, (event) => {
      if (this.elements.frequency && !this.elements.frequency.contains(event.target)) {
        this.closeFrequency();
      }
    });

    this.addDirectEvent("keydown", document, (event) => {
      if (event.key === "Escape" && this.frequencyOpen) {
        this.closeFrequency();
        this.elements.frequencyTrigger.focus();
      }
    });
  }

  get form() {
    return this.closest("form");
  }

  get subscribing() {
    return !!(this.elements.subscriptionInput && this.elements.subscriptionInput.checked);
  }

  get frequencyOpen() {
    return !!(this.elements.frequency && this.elements.frequency.classList.contains("state--open"));
  }

  selectPurchase(detail, input) {
    this.onetimeChosen = input.dataset.purchaseType === "onetime";
    this.applyPurchase();
  }

  applyPurchase() {
    this.elements.options.forEach((option) => {
      option.classList.toggle("state--active", !!option.querySelector("[data-purchase-type]:checked"));
    });

    this.sync();
  }

  selectPack(detail, pack) {
    pack.closest("[data-option-index]").querySelectorAll(".product-form-purchase-pack").forEach((element) => {
      const selected = element === pack;

      element.classList.toggle("state--active", selected);
      element.setAttribute("aria-checked", selected);
    });

    this.updatePackAvailability();

    const variant = this.onetimeVariant();

    if (!variant.title) {
      this.form.querySelector("[type=submit]").disabled = true;

      return;
    }

    this.elements.onetimePrice.textContent = variant.price;
    this.elements.onetimeCompare.textContent = variant.compare_at_price || "";
    this.elements.onetimeCompare.hidden = !variant.compare_at_price;

    this.updateSubscription(variant);
    this.sync();
  }

  updateSubscription(variant) {
    const input = this.elements.subscriptionInput;

    if (!input) {
      return;
    }

    const matches = variant.title === this.data.subscription.title;

    input.disabled = !matches;
    input.closest(".product-form-purchase").classList.toggle("state--disabled", !matches);

    if (!matches && input.checked) {
      this.closeFrequency();
      this.elements.onetimeInput.checked = true;
      this.applyPurchase();
    }

    if (matches && !input.checked && !this.onetimeChosen) {
      input.checked = true;
      this.applyPurchase();
    }
  }

  toggleFrequency() {
    if (this.frequencyOpen) {
      this.closeFrequency();

      return;
    }

    const list = this.elements.frequencyList;

    list.hidden = false;

    const room = window.innerHeight - this.elements.frequencyTrigger.getBoundingClientRect().bottom;

    this.elements.frequency.classList.toggle("state--above", room < list.scrollHeight + 40);
    this.elements.frequency.classList.add("state--open");
    this.elements.frequencyTrigger.setAttribute("aria-expanded", "true");

    const selected = list.querySelector(".state--selected") || this.elements.frequencyOptions[0];

    selected.focus();
  }

  closeFrequency() {
    if (!this.frequencyOpen) {
      return;
    }

    this.elements.frequency.classList.remove("state--open", "state--above");
    this.elements.frequencyList.hidden = true;
    this.elements.frequencyTrigger.setAttribute("aria-expanded", "false");
  }

  chooseFrequency(detail, option) {
    this.elements.frequencyOptions.forEach((element) => {
      const selected = element === option;

      element.classList.toggle("state--selected", selected);
      element.setAttribute("aria-selected", selected);
    });

    this.elements.frequencyValue.textContent = option.textContent.trim();
    this.closeFrequency();
    this.elements.frequencyTrigger.focus();
    this.sync();
  }

  get packRows() {
    return Array.from(this.querySelectorAll("[data-option-index]"));
  }

  selectedValues() {
    return this.packRows.map((row) => {
      const active = row.querySelector(".product-form-purchase-pack.state--active");

      return active ? active.dataset.value : null;
    });
  }

  findVariant(values) {
    return Object.entries(this.data.onetime).find(([, variant]) => {
      return values.every((value, index) => variant.options[index] === value);
    });
  }

  updatePackAvailability() {
    const selected = this.selectedValues();

    this.packRows.forEach((row, index) => {
      row.querySelectorAll(".product-form-purchase-pack").forEach((pack) => {
        const values = selected.slice();

        values[index] = pack.dataset.value;

        const match = this.findVariant(values);

        pack.disabled = !(match && match[1].available);
      });
    });
  }

  onetimeVariant() {
    if (!this.packRows.length) {
      const id = this.data.onetime_variant;

      return { id, ...this.data.onetime[id] };
    }

    const match = this.findVariant(this.selectedValues());

    return match ? { id: match[0], ...match[1] } : {};
  }

  sync() {
    const form = this.form;
    const id = form.querySelector("[name=id]");
    const sellingPlan = form.querySelector("[name=selling_plan]");
    const button = form.querySelector("[type=submit]");
    const previous_variant = id.value;

    if (this.subscribing) {
      const plan = this.elements.frequencyOptions.find((option) => option.classList.contains("state--selected"));

      id.value = this.data.subscription.variant;
      sellingPlan.value = plan.dataset.plan;
      button.disabled = !this.data.subscription.available;
    } else {
      const variant = this.onetimeVariant();

      id.value = variant.id;
      sellingPlan.value = "";
      button.disabled = !variant.available;
    }

    const pack = this.subscribing ? this.data.subscription.title : this.onetimeVariant().title;

    if (pack && pack !== "Default Title") {
      document.querySelectorAll("[data-product-pack]").forEach((element) => {
        element.textContent = pack;
      });
    }

    if (id.value !== previous_variant) {
      document.dispatchEvent(new CustomEvent("productform:variantchange", {
        detail: {
          variant_id: id.value
        }
      }));
    }
  }
}

customElements.define("component-product-form-purchase", ComponentProductFormPurchase);
